/* =========================================
   Options / Types
========================================= */

export interface DetectChordFromMidiNotesOptions {
	beatsPerMeasure?: number;          // default 4
	quantizeBeats?: number;            // default 0.25
	preferAccidentals?: "fromNotes" | "auto" | "sharps" | "flats";
	includeSlashBass?: boolean;        // default false
	minPcSalienceFraction?: number;    // default 0.12
	endOfMeasureBoost?: number;        // default 0.12

	// In the old “single chord” world, maxResults was “how many chords to return”.
	// In the new “multi-chord” world, maxResults is the BEAM WIDTH / how many progression candidates to return.
	maxResults?: number;               // default 8
	sliceTopK?: number;                // default 3
	sliceRankDecay?: number;           // default 0.6 (0..1), weight multiplier per rank

	// Multi-instrument (single-chord) combining (kept for legacy APIs):
	instrumentAgreementBoost?: number; // default 0.45
	instrumentSimilarityBoost?: number;// default 0.22
	triadPreferenceStrength?: number;  // default 0.35
}

export type MidiNoteLike = {
	readonly midi: number;
	readonly startBeat: number;
	readonly noteName: string;
	readonly velocity: number;
	readonly staccato: boolean;
	readonly measureNumber: number;
	getBeatDuration(): number;
};

type AccPref = "auto" | "sharps" | "flats";

const PC_NAMES_SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
const PC_NAMES_FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"] as const;
const PC_NAMES_AUTO = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"] as const;

function pcToName(pc: number, pref: AccPref): string {
	const n = ((pc % 12) + 12) % 12;
	if (pref === "sharps") return PC_NAMES_SHARP[n];
	if (pref === "flats") return PC_NAMES_FLAT[n];
	return PC_NAMES_AUTO[n];
}

function clamp01(x: number): number {
	if (!Number.isFinite(x)) return 0;
	return Math.max(0, Math.min(1, x));
}

function inferAccidentalPreference(notes: MidiNoteLike[]): AccPref {
	let sharps = 0;
	let flats = 0;
	for (const n of notes) {
		const s = (n.noteName ?? "").toLowerCase();
		if (s.includes("#")) sharps++;
		if (s.includes("b") || s.includes("-") || s.includes("♭")) flats++;
	}
	if (sharps > flats * 1.2) return "sharps";
	if (flats > sharps * 1.2) return "flats";
	return "auto";
}

function beatStrength(beat: number, beatsPerMeasure: number): number {
	const nearest = Math.round(beat);
	const dist = Math.abs(beat - nearest);

	let s =
		dist < 1e-6 ? 1.00 :
			dist < 0.125 ? 0.82 :
				dist < 0.25 ? 0.68 :
					dist < 0.375 ? 0.55 :
						0.42;

	if (Math.abs(beat - 0) < 1e-6) s *= 1.25;

	const half = beatsPerMeasure / 2;
	if (Number.isInteger(half) && Math.abs(beat - half) < 1e-6) s *= 1.08;

	return s;
}

function roundBeat(b: number): number {
	// tame floating point noise for stable comparisons
	return Math.round(b * 1_000_000) / 1_000_000;
}

/* =========================================
   Chord Templates
========================================= */

type ChordTemplate = {
	intervals: number[]; // above root (pitch-class intervals)
	required: Array<{ iv: number; sev: number }>;
	suffix: string;      // appended to root name
};

const TEMPLATES: ChordTemplate[] = [
	// Major
	{ suffix: "", intervals: [0, 4, 7], required: [{ iv: 4, sev: 2.2 }, { iv: 7, sev: 0.6 }] },
	{ suffix: "6", intervals: [0, 4, 7, 9], required: [{ iv: 4, sev: 2.2 }] },
	{ suffix: "add9", intervals: [0, 4, 7, 2], required: [{ iv: 4, sev: 2.2 }] },
	{ suffix: "6/9", intervals: [0, 4, 7, 9, 2], required: [{ iv: 4, sev: 2.2 }] },
	{ suffix: "maj7", intervals: [0, 4, 7, 11], required: [{ iv: 4, sev: 2.2 }, { iv: 11, sev: 1.5 }] },
	{ suffix: "maj9", intervals: [0, 4, 7, 11, 2], required: [{ iv: 4, sev: 2.2 }, { iv: 11, sev: 1.5 }] },
	{ suffix: "maj11", intervals: [0, 4, 7, 11, 2, 5], required: [{ iv: 4, sev: 2.2 }, { iv: 11, sev: 1.5 }] },
	{ suffix: "maj13", intervals: [0, 4, 7, 11, 2, 5, 9], required: [{ iv: 4, sev: 2.2 }, { iv: 11, sev: 1.5 }] },

	// Minor
	{ suffix: "m", intervals: [0, 3, 7], required: [{ iv: 3, sev: 2.2 }, { iv: 7, sev: 0.6 }] },
	{ suffix: "m6", intervals: [0, 3, 7, 9], required: [{ iv: 3, sev: 2.2 }] },
	{ suffix: "m(add9)", intervals: [0, 3, 7, 2], required: [{ iv: 3, sev: 2.2 }] },
	{ suffix: "m7", intervals: [0, 3, 7, 10], required: [{ iv: 3, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "m9", intervals: [0, 3, 7, 10, 2], required: [{ iv: 3, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "m11", intervals: [0, 3, 7, 10, 2, 5], required: [{ iv: 3, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "m13", intervals: [0, 3, 7, 10, 2, 5, 9], required: [{ iv: 3, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "mMaj7", intervals: [0, 3, 7, 11], required: [{ iv: 3, sev: 2.2 }, { iv: 11, sev: 1.5 }] },
	{ suffix: "mMaj9", intervals: [0, 3, 7, 11, 2], required: [{ iv: 3, sev: 2.2 }, { iv: 11, sev: 1.5 }] },

	// Dominant
	{ suffix: "7", intervals: [0, 4, 7, 10], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "9", intervals: [0, 4, 7, 10, 2], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "11", intervals: [0, 4, 7, 10, 2, 5], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "13", intervals: [0, 4, 7, 10, 2, 5, 9], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7sus4", intervals: [0, 5, 7, 10], required: [{ iv: 5, sev: 2.0 }, { iv: 10, sev: 1.2 }] },
	{ suffix: "9sus4", intervals: [0, 5, 7, 10, 2], required: [{ iv: 5, sev: 2.0 }, { iv: 10, sev: 1.2 }] },

	{ suffix: "7(b9)", intervals: [0, 4, 7, 10, 1], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7(#9)", intervals: [0, 4, 7, 10, 3], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7(#11)", intervals: [0, 4, 7, 10, 6], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7(b13)", intervals: [0, 4, 7, 10, 8], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7(b5)", intervals: [0, 4, 6, 10], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },
	{ suffix: "7(#5)", intervals: [0, 4, 8, 10], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },

	// Diminished / half-diminished / augmented
	{ suffix: "dim", intervals: [0, 3, 6], required: [{ iv: 3, sev: 2.2 }, { iv: 6, sev: 1.2 }] },
	{ suffix: "dim7", intervals: [0, 3, 6, 9], required: [{ iv: 3, sev: 2.2 }, { iv: 6, sev: 1.2 }, { iv: 9, sev: 1.2 }] },
	{ suffix: "m7b5", intervals: [0, 3, 6, 10], required: [{ iv: 3, sev: 2.2 }, { iv: 6, sev: 1.2 }, { iv: 10, sev: 1.2 }] },
	{ suffix: "aug", intervals: [0, 4, 8], required: [{ iv: 4, sev: 2.2 }, { iv: 8, sev: 1.2 }] },
	{ suffix: "maj7#5", intervals: [0, 4, 8, 11], required: [{ iv: 4, sev: 2.2 }, { iv: 11, sev: 1.5 }] },
	{ suffix: "7#5", intervals: [0, 4, 8, 10], required: [{ iv: 4, sev: 2.2 }, { iv: 10, sev: 1.5 }] },

	// Suspended / power
	{ suffix: "sus2", intervals: [0, 2, 7], required: [{ iv: 2, sev: 2.0 }] },
	{ suffix: "sus4", intervals: [0, 5, 7], required: [{ iv: 5, sev: 2.0 }] },
	{ suffix: "5", intervals: [0, 7], required: [{ iv: 7, sev: 1.0 }] },
];

type ScoredChord = {
	baseName: string;
	rootPc: number;
	chordPcs: boolean[];
	intervals: number[];
	suffix: string;
	score: number;
};

/* =========================================
   DetectedChord
========================================= */

export type TriadQuality = "maj" | "min" | "dim" | "aug" | "sus2" | "sus4" | "power" | "unknown";

function triadQualityFromIntervals(intervals: number[]): TriadQuality {
	const pcs = new Set(intervals.map((x) => ((x % 12) + 12) % 12));
	if (pcs.size === 2 && pcs.has(0) && pcs.has(7)) return "power";
	if (pcs.has(0) && pcs.has(2) && pcs.has(7)) return "sus2";
	if (pcs.has(0) && pcs.has(5) && pcs.has(7)) return "sus4";
	const has3 = pcs.has(3);
	const has4 = pcs.has(4);
	const has6 = pcs.has(6);
	const has8 = pcs.has(8);
	if (has3 && has6) return "dim";
	if (has4 && has8) return "aug";
	if (has3 && !has4) return "min";
	if (has4 && !has3) return "maj";
	return "unknown";
}

function buildAscendingMidiPitches(rootMidi: number, intervals: number[]): number[] {
	const pcs = [...new Set(intervals.map((x) => ((x % 12) + 12) % 12))];
	pcs.sort((a, b) => {
		if (a === 0 && b !== 0) return -1;
		if (b === 0 && a !== 0) return 1;
		return a - b;
	});

	const out: number[] = [];
	let prev = -Infinity;
	for (const pc of pcs) {
		let m = rootMidi + pc;
		while (m <= prev) m += 12;
		out.push(m);
		prev = m;
	}
	return out;
}

function chooseRootMidiForPcFromContext(notes: MidiNoteLike[], rootPc: number): number {
	const minMidi = Math.min(...notes.map((n) => n.midi));
	const anchorOct = Math.floor(minMidi / 12);
	let rootMidi = anchorOct * 12 + rootPc;
	if (rootMidi > minMidi) rootMidi -= 12;
	while (rootMidi < 0) rootMidi += 12;
	while (rootMidi > 127) rootMidi -= 12;
	return rootMidi;
}

function pitchClassesFromChordPcs(chordPcs: boolean[]): number[] {
	const pcs: number[] = [];
	for (let pc = 0; pc < 12; pc++) if (chordPcs[pc]) pcs.push(pc);
	return pcs;
}

export class DetectedChord {
	public readonly displayName: string;
	public readonly baseName: string;
	public readonly rootPc: number;
	public readonly slashBassPc: number | null;
	public readonly suffix: string;
	public readonly intervals: number[];
	public readonly pitchClasses: number[];
	public readonly triadQuality: TriadQuality;
	public readonly rootMidi: number;
	public readonly midiPitches: number[];
	public readonly noteNames: string[];
	public readonly isTriadLike: boolean;
	public readonly isSlash: boolean;

	private constructor(args: {
		displayName: string;
		baseName: string;
		rootPc: number;
		slashBassPc: number | null;
		suffix: string;
		intervals: number[];
		pitchClasses: number[];
		triadQuality: TriadQuality;
		rootMidi: number;
		midiPitches: number[];
		noteNames: string[];
	}) {
		this.displayName = args.displayName;
		this.baseName = args.baseName;
		this.rootPc = args.rootPc;
		this.slashBassPc = args.slashBassPc;
		this.suffix = args.suffix;
		this.intervals = args.intervals;
		this.pitchClasses = args.pitchClasses;
		this.triadQuality = args.triadQuality;
		this.rootMidi = args.rootMidi;
		this.midiPitches = args.midiPitches;
		this.noteNames = args.noteNames;

		const pcs = new Set(this.intervals.map((x) => ((x % 12) + 12) % 12));
		const has7th = pcs.has(10) || pcs.has(11);
		const has9th = pcs.has(2) || pcs.has(1) || pcs.has(3);
		const has11th = pcs.has(5) || pcs.has(6);
		const has13th = pcs.has(9) || pcs.has(8);
		this.isTriadLike = !has7th && !has9th && !has11th && !has13th;
		this.isSlash = this.slashBassPc != null && this.slashBassPc !== this.rootPc;
	}

	public stableKey(): string {
		return `${this.rootPc}:${this.suffix}:${this.slashBassPc ?? "x"}`;
	}

	/** "Same chord" identity ignoring slash bass (useful for change detection). */
	public harmonyKey(): string {
		return `${this.rootPc}:${this.suffix}`;
	}

	public static fromScoredChord(args: {
		exemplar: ScoredChord;
		pref: AccPref;
		includeSlashBass: boolean;
		globalBassPc: number | null;
		contextNotes: MidiNoteLike[];
	}): DetectedChord {
		const { exemplar, pref, includeSlashBass, globalBassPc, contextNotes } = args;

		let slashBassPc: number | null = null;
		if (includeSlashBass && globalBassPc != null) {
			if (globalBassPc !== exemplar.rootPc && exemplar.chordPcs[globalBassPc]) {
				const bassIv = (globalBassPc - exemplar.rootPc + 12) % 12;
				const stable = bassIv === 3 || bassIv === 4 || bassIv === 6 || bassIv === 7 || bassIv === 8 || bassIv === 10 || bassIv === 11;
				if (stable) slashBassPc = globalBassPc;
			}
		}

		const rootMidi = chooseRootMidiForPcFromContext(contextNotes, exemplar.rootPc);
		const midiPitches = buildAscendingMidiPitches(rootMidi, exemplar.intervals);
		const pitchClasses = pitchClassesFromChordPcs(exemplar.chordPcs);
		const noteNames = pitchClasses.map((pc) => pcToName(pc, pref));
		const triadQuality = triadQualityFromIntervals(exemplar.intervals);

		const baseName = exemplar.baseName;
		const displayName = slashBassPc == null ? baseName : `${baseName}/${pcToName(slashBassPc, pref)}`;

		return new DetectedChord({
			displayName,
			baseName,
			rootPc: exemplar.rootPc,
			slashBassPc,
			suffix: exemplar.suffix,
			intervals: [...exemplar.intervals],
			pitchClasses,
			triadQuality,
			rootMidi,
			midiPitches,
			noteNames
		});
	}
}

/* =========================================
   Beat-mapped chord changes
========================================= */

export class BeatMappedDetectedChord {
	public readonly chord: DetectedChord;
	public readonly startBeat: number; // can be decimal

	public constructor(chord: DetectedChord, startBeat: number) {
		this.chord = chord;
		this.startBeat = roundBeat(startBeat);
	}

	public harmonyKey(): string {
		return this.chord.harmonyKey();
	}
}

export type ProgressionEqualityMode = "exact" | "harmonyExact";

function compactConsecutive(items: BeatMappedDetectedChord[]): BeatMappedDetectedChord[] {
	const out: BeatMappedDetectedChord[] = [];
	for (const x of items) {
		const prev = out[out.length - 1];
		if (!prev || prev.harmonyKey() !== x.harmonyKey()) out.push(x);
	}
	return out;
}

function progressionKeyExact(items: ReadonlyArray<BeatMappedDetectedChord>): string {
	return items.map((x) => `${x.startBeat}@${x.chord.stableKey()}`).join("|");
}

function progressionKeyHarmonyExact(items: ReadonlyArray<BeatMappedDetectedChord>): string {
	return items.map((x) => `${x.startBeat}@${x.chord.harmonyKey()}`).join("|");
}

/**
 * Wrapper around a chord-change list with O(1) equality checks.
 *
 * Normalizes:
 * - removes consecutive duplicate harmonies
 * - forces first chord to beat 0 (your rule)
 */
export class BeatMappedChordProgression {
	public readonly items: ReadonlyArray<BeatMappedDetectedChord>;

	private readonly _keyExact: string;
	private readonly _keyHarmonyExact: string;

	public constructor(items: BeatMappedDetectedChord[]) {
		const compact = compactConsecutive(items);
		if (compact.length > 0 && compact[0].startBeat !== 0) {
			compact[0] = new BeatMappedDetectedChord(compact[0].chord, 0);
		}

		this.items = compact;
		this._keyExact = progressionKeyExact(compact);
		this._keyHarmonyExact = progressionKeyHarmonyExact(compact);
	}

	private _displayNameCache: string | null = null;
	public getDisplayName(): string {
		if (this.items.length === 0) return "(no chords)";
		if (this._displayNameCache === null) {
			this._displayNameCache = this.items.map((x) => `${x.chord.displayName}${x.startBeat > 0 ? `(${x.startBeat})` : ""}`).join(" ");
		}
		return this._displayNameCache;
	}

	public isEmpty(): boolean {
		return this.items.length === 0;
	}

	public key(mode: ProgressionEqualityMode = "exact"): string {
		return mode === "exact" ? this._keyExact : this._keyHarmonyExact;
	}

	public equals(other: BeatMappedChordProgression, mode: ProgressionEqualityMode = "exact"): boolean {
		return this.key(mode) === other.key(mode);
	}
}

/* =========================================
   Low-level helpers
========================================= */

function overlap(a0: number, a1: number, b0: number, b1: number): number {
	const lo = Math.max(a0, b0);
	const hi = Math.min(a1, b1);
	return Math.max(0, hi - lo);
}

function normalizePcWeightsWithSalienceCut(pcWeights: number[], minFrac: number): number[] {
	const maxW = Math.max(...pcWeights);
	if (maxW <= 1e-9) return pcWeights;

	const cut = maxW * minFrac;
	const kept = pcWeights.map((w) => (w >= cut ? w : 0));

	const keptCount = kept.reduce((c, w) => c + (w > 1e-9 ? 1 : 0), 0);
	if (keptCount >= 2) return kept;

	return pcWeights;
}

function scoreAllChordsForSlice(
	pcWeights: number[],
	bassPc: number | null,
	pref: AccPref
): ScoredChord[] {
	const totalW = pcWeights.reduce((a, b) => a + b, 0);
	if (totalW <= 1e-9) return [];

	const presentCount = pcWeights.reduce((c, w) => c + (w > 1e-9 ? 1 : 0), 0);
	const avgW = totalW / Math.max(1, presentCount);

	const results: ScoredChord[] = [];

	for (let rootPc = 0; rootPc < 12; rootPc++) {
		const rootName = pcToName(rootPc, pref);

		for (const t of TEMPLATES) {
			const chordPcs = Array.from({ length: 12 }, () => false) as boolean[];
			for (const iv of t.intervals) chordPcs[(rootPc + iv) % 12] = true;

			let explained = 0;
			for (let pc = 0; pc < 12; pc++) if (chordPcs[pc]) explained += pcWeights[pc];
			const extra = totalW - explained;

			let missingSev = 0;
			for (const r of t.required) {
				const pc = (rootPc + r.iv) % 12;
				if (pcWeights[pc] <= 1e-9) missingSev += r.sev;
			}

			const rootW = pcWeights[rootPc];
			const rootBonus = rootW * 0.25;

			let bassBonus = 0;
			if (bassPc != null) {
				if (chordPcs[bassPc]) bassBonus += avgW * (bassPc === rootPc ? 0.9 : 0.55);
				else bassBonus -= avgW * 0.75;
			}

			const complexity = (t.intervals.length - 1);
			const complexityPenalty = complexity * avgW * 0.14;

			const extraPenalty = extra * 1.25;
			const missingPenalty = missingSev * avgW * 1.05;

			const score = explained + rootBonus + bassBonus - extraPenalty - missingPenalty - complexityPenalty;
			const baseName = `${rootName}${t.suffix}`;

			results.push({
				baseName,
				rootPc,
				chordPcs,
				intervals: t.intervals,
				suffix: t.suffix,
				score
			});
		}
	}

	results.sort((a, b) => b.score - a.score);
	return results;
}

function buildSinglePcScoredChord(pref: AccPref, pc: number): ScoredChord {
	const baseName = pcToName(pc, pref);
	return {
		baseName,
		rootPc: pc,
		chordPcs: Array.from({ length: 12 }, (_, i) => i === pc),
		intervals: [0],
		suffix: "",
		score: 0
	};
}

/* =========================================
   Progression detection (single stream)
========================================= */

type SliceChordOption = {
	chord: DetectedChord;
	emission: number;
	harmonyKey: string; // chord.harmonyKey()
};

type BeamState = {
	score: number;
	lastKey: string | null;
	segments: BeatMappedDetectedChord[];
};

/**
 * Returns progression candidates (best-first) for a single note stream.
 */
export function detectBeatMappedChordProgressionCandidatesFromMidiNotes(
	notes: MidiNoteLike[],
	options: DetectChordFromMidiNotesOptions = {}
): BeatMappedChordProgression[] {
	const beatsPerMeasure = options.beatsPerMeasure ?? 4;
	const quantizeBeats = options.quantizeBeats ?? 0.25;
	const minPcFrac = options.minPcSalienceFraction ?? 0.12;
	const endBoost = options.endOfMeasureBoost ?? 0.12;

	const maxResults = options.maxResults ?? 8;
	const sliceTopK = Math.max(1, options.sliceTopK ?? 3);
	const decay = Math.max(0, Math.min(1, options.sliceRankDecay ?? 0.6));
	const includeSlashBass = options.includeSlashBass ?? false;

	const filtered = (notes ?? [])
		.filter((n) => Number.isFinite(n.midi) && n.midi >= 0 && n.midi <= 127)
		.filter((n) => Number.isFinite(n.startBeat) && Number.isFinite(n.getBeatDuration?.()))
		.filter((n) => n.getBeatDuration() > 0);

	if (filtered.length === 0) return [];

	const pref: AccPref = (() => {
		const p = options.preferAccidentals ?? "fromNotes";
		if (p === "fromNotes") return inferAccidentalPreference(filtered);
		return p;
	})();

	// Absolute timeline in beats: map measureNumber -> index in sorted order
	const measureNumbers = [...new Set(filtered.map((n) => n.measureNumber))].sort((a, b) => a - b);
	const measureIndexByNumber = new Map<number, number>();
	for (let i = 0; i < measureNumbers.length; i++) measureIndexByNumber.set(measureNumbers[i], i);

	const absNotes = filtered.map((n) => {
		const idx = measureIndexByNumber.get(n.measureNumber) ?? 0;
		const absStart = idx * beatsPerMeasure + n.startBeat;
		const absEnd = absStart + n.getBeatDuration();
		return { n, absStart, absEnd };
	});

	const endAbs = Math.max(...absNotes.map((x) => x.absEnd), beatsPerMeasure);

	// Penalize chord changes to avoid jitter
	const CHANGE_PENALTY_FACTOR = 0.35;

	let beam: BeamState[] = [{ score: 0, lastKey: null, segments: [] }];

	for (let t0 = 0; t0 < endAbs + 1e-9; t0 += quantizeBeats) {
		const t1 = t0 + quantizeBeats;

		let sliceBassMidi: number | null = null;
		const active: MidiNoteLike[] = [];

		for (const x of absNotes) {
			const ov = overlap(x.absStart, x.absEnd, t0, t1);
			if (ov <= 1e-9) continue;
			active.push(x.n);
			if (sliceBassMidi == null || x.n.midi < sliceBassMidi) sliceBassMidi = x.n.midi;
		}

		// No evidence -> skip slice
		if (active.length === 0) continue;

		const sliceBassPc = sliceBassMidi != null ? ((sliceBassMidi % 12) + 12) % 12 : null;

		const pcWeights = new Array<number>(12).fill(0);
		const beatInMeasure = ((t0 % beatsPerMeasure) + beatsPerMeasure) % beatsPerMeasure;
		const bs = beatStrength(beatInMeasure, beatsPerMeasure);
		const posInMeasure = beatsPerMeasure > 1e-9 ? (beatInMeasure / beatsPerMeasure) : 0;
		const measurePosWeight = 1.0 + endBoost * (posInMeasure * posInMeasure);

		for (const n of active) {
			const idx = measureIndexByNumber.get(n.measureNumber) ?? 0;
			const ns = idx * beatsPerMeasure + n.startBeat;
			const ne = ns + n.getBeatDuration();
			const ov = overlap(ns, ne, t0, t1);
			if (ov <= 1e-9) continue;

			const vel = clamp01(n.velocity);
			const pc = ((n.midi % 12) + 12) % 12;

			const isSliceBass = sliceBassMidi != null && n.midi === sliceBassMidi;
			const bassBoost = isSliceBass ? 1.35 : 1.0;
			const treblePenalty = sliceBassMidi != null
				? (1.0 - 0.15 * clamp01((n.midi - sliceBassMidi) / 48))
				: 1.0;

			const w = ov * vel * bs * measurePosWeight * bassBoost * treblePenalty;
			pcWeights[pc] += w;
		}

		const cleaned = normalizePcWeightsWithSalienceCut(pcWeights, minPcFrac);
		const totalW = cleaned.reduce((a, b) => a + b, 0);
		const presentCount = cleaned.reduce((c, w) => c + (w > 1e-9 ? 1 : 0), 0);
		if (totalW <= 1e-9 || presentCount === 0) continue;

		const sliceOptions: SliceChordOption[] = [];

		if (presentCount >= 2) {
			const scored = scoreAllChordsForSlice(cleaned, sliceBassPc, pref);
			const top = scored.slice(0, sliceTopK);

			for (let rank = 0; rank < top.length; rank++) {
				const exemplar = top[rank];
				const chordObj = DetectedChord.fromScoredChord({
					exemplar,
					pref,
					includeSlashBass,
					globalBassPc: sliceBassPc,
					contextNotes: filtered
				});

				sliceOptions.push({
					chord: chordObj,
					emission: totalW * Math.pow(decay, rank),
					harmonyKey: chordObj.harmonyKey()
				});
			}
		} else {
			const pc = cleaned.findIndex((w) => w > 1e-9);
			if (pc >= 0) {
				const exemplar = buildSinglePcScoredChord(pref, pc);
				const chordObj = DetectedChord.fromScoredChord({
					exemplar,
					pref,
					includeSlashBass: false,
					globalBassPc: null,
					contextNotes: filtered
				});
				sliceOptions.push({ chord: chordObj, emission: totalW, harmonyKey: chordObj.harmonyKey() });
			}
		}

		if (sliceOptions.length === 0) continue;

		const next: BeamState[] = [];

		for (const state of beam) {
			for (const opt of sliceOptions) {
				const changed = state.lastKey != null && state.lastKey !== opt.harmonyKey;
				const penalty = changed ? (totalW * CHANGE_PENALTY_FACTOR) : 0;
				const newScore = state.score + opt.emission - penalty;

				const segs = state.segments.slice();
				if (state.lastKey == null) {
					// Caveat: first chord always maps to beat 0
					segs.push(new BeatMappedDetectedChord(opt.chord, 0));
				} else if (changed) {
					segs.push(new BeatMappedDetectedChord(opt.chord, t0));
				}

				next.push({ score: newScore, lastKey: opt.harmonyKey, segments: segs });
			}

			// Allow "hold" to ignore weak evidence slices (helps rhythmic patterns)
			if (state.lastKey != null) {
				next.push({ score: state.score + totalW * 0.05, lastKey: state.lastKey, segments: state.segments });
			}
		}

		next.sort((a, b) => b.score - a.score);
		beam = next.slice(0, Math.max(8, maxResults * 3));
	}

	beam.sort((a, b) => b.score - a.score);

	// Build candidate progressions and de-dupe by harmony-exact (ignore slash) for variety.
	const seen = new Set<string>();
	const out: BeatMappedChordProgression[] = [];

	for (const b of beam) {
		const prog = new BeatMappedChordProgression(b.segments);
		if (prog.isEmpty()) continue;

		const sig = prog.key("harmonyExact");
		if (seen.has(sig)) continue;
		seen.add(sig);

		out.push(prog);
		if (out.length >= maxResults) break;
	}

	return out;
}

/** Best single-stream progression (best candidate). */
export function detectBestBeatMappedChordProgressionFromMidiNotes(
	notes: MidiNoteLike[],
	options: DetectChordFromMidiNotesOptions = {}
): BeatMappedChordProgression {
	return detectBeatMappedChordProgressionCandidatesFromMidiNotes(notes, options)[0] ?? new BeatMappedChordProgression([]);
}

/* =========================================
   Progression detection (instrumentGroups, EXACT agreement)
========================================= */

/**
 * Each instrument contributes ONE best progression.
 * Progressions are considered the same ONLY if beats and DetectedChord.stableKey() match exactly.
 */
export function detectBeatMappedChordProgressionCandidatesFromInstrumentGroups(
	instrumentGroups: MidiNoteLike[][],
	options: DetectChordFromMidiNotesOptions = {}
): Array<{ progression: BeatMappedChordProgression; confidence: number; votes: number }> {
	const groups = (instrumentGroups ?? []).filter((g) => (g?.length ?? 0) > 0);
	if (groups.length === 0) return [];

	const maxResults = options.maxResults ?? 8;

	const voteMap = new Map<string, { progression: BeatMappedChordProgression; votes: number }>();

	for (const g of groups) {
		const prog = detectBestBeatMappedChordProgressionFromMidiNotes(g, options);
		if (prog.isEmpty()) continue;

		const sig = prog.key("exact");
		const existing = voteMap.get(sig);
		if (existing) existing.votes += 1;
		else voteMap.set(sig, { progression: prog, votes: 1 });
	}

	const total = groups.length;

	return [...voteMap.values()]
		.sort((a, b) => b.votes - a.votes)
		.slice(0, Math.max(1, maxResults))
		.map((x) => ({
			progression: x.progression,
			votes: x.votes,
			confidence: x.votes / total
		}));
}

export function detectBestBeatMappedChordProgressionFromInstrumentGroups(
	instrumentGroups: MidiNoteLike[][],
	options: DetectChordFromMidiNotesOptions = {}
): BeatMappedChordProgression {
	const candidates = detectBeatMappedChordProgressionCandidatesFromInstrumentGroups(instrumentGroups, options);
	if (candidates.length > 0) return candidates[0].progression;

	// Fallback: flatten and run single-stream
	const flattened = (instrumentGroups ?? []).flat();
	return detectBestBeatMappedChordProgressionFromMidiNotes(flattened, options);
}